/**
 * ★ "YOUR AGREEMENTS" (design 25; design 24, T24's fourth place - the owner's E1: "one place to see
 * them all"). Every document this author has accepted or owes, from `listAgreements` (the support
 * feature's read, features/support): its name in the cabinet's words, "Version X, accepted on
 * <day>" with "Read it" - the version they accepted, in a new tab - and, where there is one, "A new
 * version is waiting" with "Review and accept".
 *
 * WHERE "REVIEW AND ACCEPT" GOES follows what the document holds back (`blocks`): a submission
 * document (the author agreement, the editorial standards) is accepted on the terms interstitial,
 * `/terms`; a payout document (the payout terms, the payment-data notice) on Payment details,
 * where its box is (design 25, section 6).
 *
 * THE DAY is written as the rest of the cabinet writes one (`formatDay`, the cabinet's language),
 * from the UTC day of the moment the server recorded - never the browser's clock.
 *
 * Read-only: nothing here accepts anything. Its own failure is its own: a list that will not load
 * leaves the rest of Settings as it is.
 */
import { Anchor, Group, List, Stack, Text } from '@mantine/core';
import { FileText } from '@moonx/ui';
import { TERMS_PATH } from '../../features/auth/navigation';
import { ErrorState, LoadingState, SectionCard } from '../../features/cabinet/components';
import { formatDay } from '../../features/cabinet/format';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { agreementName, useAgreements } from '../../features/support';
import type { Agreement } from '../../features/support';
import { PAYOUT_METHOD_PATH } from '../payments/PayoutMethodRoute';
import { useTranslation } from '../../i18n';

/** "Oct 11, 2026": the UTC day of a recorded moment, as the cabinet writes a day. */
function dayOf(instant: string): string {
  return formatDay(instant.slice(0, 10));
}

/** Where a document is accepted: the terms interstitial, or Payment details for a payout one. */
function acceptHref(agreement: Agreement): string {
  return agreement.blocks === 'payout' ? PAYOUT_METHOD_PATH : TERMS_PATH;
}

function AgreementRow({ agreement }: { readonly agreement: Agreement }) {
  const { t } = useTranslation('profile');
  const accepted = agreement.accepted;
  const readHref = accepted?.contentUrl ?? agreement.live?.contentUrl;
  const toAccept = agreement.newerVersionWaiting || (accepted === null && agreement.owed);
  return (
    <List.Item>
      <Stack gap={2}>
        <h3 style={{ margin: 0, fontSize: 'var(--mantine-font-size-sm)', fontWeight: 700 }}>
          {agreementName(agreement)}
        </h3>
        <Text size="sm" c="dimmed">
          {accepted === null
            ? t('settings.agreements.notAccepted')
            : t('settings.agreements.accepted', {
                version: accepted.versionLabel,
                day: dayOf(accepted.acceptedAt),
              })}
        </Text>
        <Group gap="md">
          {readHref === undefined ? null : (
            <Anchor href={readHref} target="_blank" rel="noopener noreferrer" size="sm">
              {t('settings.agreements.read')}
            </Anchor>
          )}
          {toAccept ? (
            <>
              {agreement.newerVersionWaiting ? (
                <Text size="sm" fw={600}>
                  {t('settings.agreements.newVersion')}
                </Text>
              ) : null}
              <InAppAnchor href={acceptHref(agreement)} size="sm">
                {t('settings.agreements.review')}
              </InAppAnchor>
            </>
          ) : null}
        </Group>
      </Stack>
    </List.Item>
  );
}

export function AgreementsSection() {
  const { t } = useTranslation('profile');
  const agreements = useAgreements();
  return (
    <SectionCard title={t('settings.agreements.title')} icon={FileText}>
      {agreements.status === 'loading' && agreements.data === null ? (
        <LoadingState label={t('settings.agreements.reading')} rows={2} />
      ) : agreements.status === 'failed' ? (
        <ErrorState
          title={t('settings.agreements.failed')}
          error={agreements.error}
          onRetry={agreements.reload}
        />
      ) : agreements.data === null || agreements.data.items.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('settings.agreements.none')}
        </Text>
      ) : (
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            {t('settings.agreements.note')}
          </Text>
          <List listStyleType="none" spacing="md" p={0}>
            {agreements.data.items.map((agreement) => (
              <AgreementRow key={agreement.documentCode} agreement={agreement} />
            ))}
          </List>
        </Stack>
      )}
    </SectionCard>
  );
}
